import { Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonModule, DecimalPipe } from '@angular/common';
import { InvoiceStatusComponent } from "../../../invoice/components/invoice-status/invoice-status.component";
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { LabeledInputComponent } from '../../../../shared/components/labeled-input/labeled-input.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { PaymentService } from '../../../../core/api/payment.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { BillingHistoryGet, Country, Customer, PayableLocationsGet, PaymentMethodGet, PaymentMethodUpdateGet, PaymentPlan, SubscriptionGet } from '../../../../core/interfaces/payment-interfaces';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { combineLatest, lastValueFrom } from 'rxjs';
import { UserDataService } from '../../../../core/services/user-data.service';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { PaymentMethodUpdateDialogComponent } from '../../components/dialogs/payment-method-update-dialog/payment-method-update-dialog.component';
import { PaymentCardComponent } from "../../components/payment-card/payment-card.component";
import { DeleteCardDialogComponent } from '../../components/dialogs/delete-card-dialog/delete-card-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { IsoToFormattedPeriodPipe } from '../../../../core/pipes/iso-to-formatted-period.pipe';
import { IsoToFormattedDatePipe } from '../../../../core/pipes/iso-to-formatted-date.pipe';
import { SubscriptionService } from '../../../../core/services/subscription.service';

@Component({
  selector: 'app-settings-billing-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, TranslocoPipe, CommonModule, InvoiceStatusComponent, CustomFooterComponent, LabeledInputComponent, CustomDropdownComponent, CustomButtonComponent, ToastComponent, PaymentMethodUpdateDialogComponent, PaymentCardComponent, DeleteCardDialogComponent, IsoToFormattedPeriodPipe, IsoToFormattedDatePipe],
  providers: [DecimalPipe],
  templateUrl: './settings-billing-page.component.html',
  styleUrl: './settings-billing-page.component.css'
})
export class SettingsBillingPageComponent implements OnInit {
  selectedSidebarItemId: number = 13;

  // Text inputs
  billingEmail: string = '';
  billingEmailError: string | null = null;

  billingName: string = '';
  billingNameError: string | null = null;

  billingCity: string = '';
  billingCityError: string | null = null;

  selectedCountry: number | undefined;
  countriesOptions: Country[] = [];
  countriesDropdownOptions: string[] = [];
  selectedCountryError: string | null = null;

  billingAddress: string = '';
  billingAddressError: string | null = null;

  billingPostalCode: string = '';
  billingPostalCodeError: string | null = null;

  billingState: string = '';
  billingStateError: string | null = null;

  billingPecEmailAddress: string = '';
  billingPecEmailAddressError: string | null = null;

  billingSdi: string = '';
  billingSdiError: string | null = null;

  billingVatId: string = '';
  billingVatIdError: string | null = null;

  // Edit
  editingInputDisabled: boolean = true;
  isUserEditing: boolean = false;
  saveClicked: boolean = false;

  // Customer
  customerData?: Customer;
  customerDataLoaded: boolean = false;
  customerExists: boolean = true;

  // Subscription information
  currentPlan = '';
  validUntil = '';
  cancelAtRenewal = '';
  billingCycle = '';
  currentPeriodStart = '';
  currentPeriodEnd = '-';
  amount = '-';
  paymentMethod = '';
  status = '';

  activeSubscription: SubscriptionGet | undefined;
  cancelResumeClicked: boolean = false;

  subscriptionNotActive: boolean = false;

  // PaymentMethodUpdate Dialog
  @ViewChild(PaymentMethodUpdateDialogComponent) paymentMethodUpdateDialog!: PaymentMethodUpdateDialogComponent;

  // Payment Methods
  paymentMethods: PaymentMethodGet[] | undefined = undefined;
  cardButtonClicked: boolean = false;

  @ViewChild(DeleteCardDialogComponent) deleteCardDialog!: DeleteCardDialogComponent;

  // Billing History
  billingHistoryDataIsLoading: boolean = true;
  lastLoadedPageNumber: number = 0;
  loadMoreHidden: boolean = false;
  listOfBillingHistory: BillingHistoryGet[] = [];

  // Translation
  clickEditTitle = 'Click the Edit button to modify the data, then save your changes.';

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  get nextBillingDate(): string {
    if (this.activeSubscription) {
      if (this.activeSubscription.cancel_at_period_end) {
        return '-';
      }
      return this.currentPeriodEnd;
    }
    return '-';
  }

  constructor(private paymentService: PaymentService, private apiError: ApiErrorService, private translocoService: TranslocoService, private authValidator: AuthValidatorService, private commonValidator: CommonValidatorService, private userData: UserDataService, private dateService: DateConversionService, private route: ActivatedRoute, private decimalPipe: DecimalPipe, private subscriptionService: SubscriptionService) { }

  ngOnInit(): void {
    this.loadBillingHisotry();

    // Check query params for succeded payment
    const redirectStatus = this.route.snapshot.queryParamMap.get('redirect_status');
    if (redirectStatus === 'succeeded') {
      this.saveClicked = true;
      this.cardButtonClicked = true;
      setTimeout(() => {
        this.loadSubscriptionData();
        this.loadPaymentMethods();
        this.saveClicked = false;
        this.cardButtonClicked = false;
      }, 4000);
    }

    this.loadCountryCodes();
    this.loadSubscriptionData();
    this.loadPaymentMethods();

    combineLatest([
      this.translocoService.selectTranslate('admin.click_edit')
    ]).subscribe((
      [
        clickEditTranslate
      ]
    ) => {
      this.clickEditTitle = clickEditTranslate;
    });
  }

  loadCountryCodes() {
    this.paymentService.getCountryCodes().subscribe({
      next: (response) => {
        if (response) {
          this.countriesOptions = response;
          this.countriesDropdownOptions = response.map(c => c.name ? `${c.name} (${c.code})` : c.code);
          this.loadCustomerData();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
      }
    });
  }

  loadCustomerData() {
    this.paymentService.getCustomer().subscribe({
      next: (response) => {
        if (response) {
          this.customerData = response;
          this.populateCustomerInputs(this.customerData);
          this.customerDataLoaded = true;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          // Customer does not exist yet
          if (error.error.code == 'SVC-4004') {
            this.customerExists = false;
            showError = false;
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        this.customerDataLoaded = true;
      },
      complete: () => {
      }
    });
  }

  populateCustomerInputs(customerData?: Customer): void {
    if (!customerData) return;

    this.billingEmail = customerData.email || '';

    this.billingName = customerData.name || '';

    this.billingCity = customerData.city || '';

    this.selectedCountry = this.countriesOptions.findIndex(c => c.code === customerData.country);
    if (this.selectedCountry === -1) this.selectedCountry = undefined;

    this.billingAddress = customerData.address || '';

    this.billingPostalCode = customerData.postal_code || '';

    this.billingState = customerData.state || '';

    this.billingPecEmailAddress = customerData.pec_email_address || '';

    this.billingSdi = customerData.sdi || '';

    this.billingVatId = customerData.vat_id || '';
  }

  getCustomerFromInputs(): Customer {
    return {
      id: this.customerData?.id ?? '',
      email: this.billingEmail.trim(),
      name: this.billingName.trim(),
      city: this.billingCity.trim(),
      country: this.selectedCountry !== undefined ? this.countriesOptions[this.selectedCountry].code : '',
      address: this.billingAddress.trim(),
      postal_code: this.billingPostalCode.trim(),
      state: this.billingState.trim(),
      pec_email_address: this.billingPecEmailAddress.trim(),
      sdi: this.billingSdi.trim(),
      vat_id: this.billingVatId.trim(),
    };
  }

  resetCustomerInputErrors(): void {
    this.billingEmailError = null;
    this.billingNameError = null;
    this.billingCityError = null;
    this.selectedCountryError = null;
    this.billingAddressError = null;
    this.billingPostalCodeError = null;
    this.billingStateError = null;
    this.billingPecEmailAddressError = null;
    this.billingSdiError = null;
    this.billingVatIdError = null;
  }


  async onEditClick(): Promise<void> {
    if (!this.customerDataLoaded) {
      return;
    }

    if (this.saveClicked) {
      return;
    }

    this.editingInputDisabled = !this.editingInputDisabled;
    this.isUserEditing = !this.isUserEditing;

    if (this.isUserEditing) {
      return;
    }

    // Save data if user is done with editing
    this.saveClicked = true;

    this.resetCustomerInputErrors();

    const billingEmailValue = this.billingEmail.trim();
    const billingNameValue = this.billingName.trim();
    const billingCityValue = this.billingCity.trim();
    const billingAddressValue = this.billingAddress.trim();
    const billingPostalCodeValue = this.billingPostalCode.trim();
    const billingStateValue = this.billingState.trim();
    const billingPecEmailAddressValue = this.billingPecEmailAddress.trim();
    const billingSdiValue = this.billingSdi.trim();
    const billingVatIdValue = this.billingVatId.trim();

    this.billingEmailError = this.authValidator.validateEmail(billingEmailValue);
    this.billingNameError = this.commonValidator.validateLength(billingNameValue, 2, 255);
    this.billingCityError = this.commonValidator.validateLength(billingCityValue, 2, 255);
    this.billingAddressError = this.commonValidator.validateLength(billingAddressValue, 2, 255);
    this.billingPostalCodeError = this.commonValidator.validateLength(billingPostalCodeValue, 2, 255);
    this.billingStateError = this.commonValidator.validateLength(billingStateValue, 2, 255);
    this.billingPecEmailAddressError = this.commonValidator.validateLength(billingPecEmailAddressValue, 2, 255);
    this.billingSdiError = this.commonValidator.validateLength(billingSdiValue, 2, 255);
    this.billingVatIdError = this.commonValidator.validateLength(billingVatIdValue, 2, 255);

    if (this.selectedCountry === undefined) {
      this.selectedCountryError = this.translocoService.translate('shared.validators.common.required');
    }

    if (
      this.billingEmailError || this.billingNameError || this.billingCityError ||
      this.billingAddressError || this.billingPostalCodeError || this.billingStateError ||
      this.billingPecEmailAddressError || this.billingSdiError || this.billingVatIdError || this.selectedCountryError) {
      this.saveClicked = false;

      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }

    try {
      if (this.customerExists) {
        // Update data
        await lastValueFrom(this.paymentService.updateCustomer(this.getCustomerFromInputs()));
      }
      else {
        // Create customer
        await lastValueFrom(this.paymentService.createCustomer(this.getCustomerFromInputs()));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.saveClicked = false;
      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }

    this.saveClicked = false;
  }

  loadSubscriptionData() {
    const userMembership = this.userData.getUser()?.membership;
    this.currentPlan = userMembership?.membership_type ?? '';
    this.validUntil = this.dateService.isoToFormattedDate(userMembership?.valid_to ?? '');
    this.cancelAtRenewal = '';

    if (!this.subscriptionService.hasPlusSubscription()) {
      this.subscriptionNotActive = true;
    }

    this.paymentService.getActiveSubscription().subscribe({
      next: (response) => {
        if (response) {
          this.activeSubscription = response;

          if (this.activeSubscription !== undefined) {
            this.cancelAtRenewal = this.activeSubscription!.cancel_at_period_end ? this.translocoService.translate('shared.yes') : this.translocoService.translate('shared.no') ?? this.translocoService.translate('shared.not_available');
            this.currentPeriodStart = this.dateService.isoToFormattedDate(this.activeSubscription?.current_period_start ?? '');
            this.currentPeriodEnd = this.dateService.isoToFormattedDate(this.activeSubscription?.current_period_end ?? '');
            this.paymentMethod = this.translocoService.translate('shared.not_available');
          }
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (error.error.code == 'SVC-4005') {
            showError = false;
          }
        }
        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      },
      complete: () => {
        this.loadPlanCost()
      }
    });
  }

  async cancelSubscription() {
    if (this.cancelResumeClicked) {
      return;
    }
    this.cancelResumeClicked = true;

    try {
      await lastValueFrom(this.paymentService.cancelSubscription());
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.cancelResumeClicked = false;
      return;
    }
    this.activeSubscription = undefined;
    setTimeout(() => {
      this.loadSubscriptionData();
      this.cancelResumeClicked = false;
    }, 3000);
    this.toast.showToast("shared.success", "settings.subscription.subscription_canceled", 'success', 4000, true);
  }

  async resumeSubscription() {
    if (this.cancelResumeClicked) {
      return;
    }
    this.cancelResumeClicked = true;

    try {
      await lastValueFrom(this.paymentService.resumeSubscription());
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        if (error.error.code == 'SVC-4010') {
          this.toast.showToast('settings.missing_default_payment_method', 'settings.missing_default_payment_method_desc', 'error', 4000, true);
          showError = false;
        }
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.cancelResumeClicked = false;
      return;
    }
    this.activeSubscription = undefined;
    setTimeout(() => {
      this.loadSubscriptionData();
      this.cancelResumeClicked = false;
    }, 3000);
    this.toast.showToast("shared.success", "settings.subscription.subscription_resumed", 'success', 4000, true);
  }

  async updatePaymentMethod() {
    var paymentMethodUpdateData: PaymentMethodUpdateGet | undefined = undefined;
    try {
      paymentMethodUpdateData = await lastValueFrom(this.paymentService.updatePaymentMethod());
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code == 'SVC-4004') {
          showError = false;
          this.toast.showToast("shared.error", "settings.subscription.customer_missing", 'error', 4000, true);
        }
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      return;
    }

    if (paymentMethodUpdateData?.client_secret) {
      this.paymentMethodUpdateDialog.openModal(paymentMethodUpdateData);
    }
  }

  loadPaymentMethods() {
    this.paymentService.getPaymentMethods().subscribe({
      next: (response) => {
        if (response) {
          this.paymentMethods = response.cards;

          const defaultCard = this.paymentMethods?.find(card => card.is_default);
          if (defaultCard) {
            this.paymentMethod = `${defaultCard.brand.toUpperCase()} **** ${defaultCard.last4}`;
          }
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          if (error.error.code == 'SVC-4000') {
            this.toast.showToast('settings.missing_default_payment_method', 'settings.missing_default_payment_method_desc', 'warning', 4000, true);
            showError = false;
          }
          else if (error.error.code == 'SVC-4004') {
            showError = false;
          }
        }
        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      },
      complete: () => {
      }
    });
  }

  async setAsDefaultClick(paymentMethod: PaymentMethodGet) {
    if (this.cardButtonClicked) {
      return;
    }
    this.cardButtonClicked = true;

    try {
      await lastValueFrom(this.paymentService.setDefaultPaymentMethod(paymentMethod.id));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.cardButtonClicked = false;
      return;
    }

    this.paymentMethods = undefined;
    setTimeout(() => {
      this.loadPaymentMethods();
      this.loadSubscriptionData();
      this.cardButtonClicked = false;
    }, 2000);
  }

  async deletePaymentMethod(paymentMethod: PaymentMethodGet) {
    if (this.cardButtonClicked) {
      return;
    }
    this.cardButtonClicked = true;

    var confirmation = await this.deleteCardDialog.openModal();

    if (!confirmation) {
      this.cardButtonClicked = false;
      return;
    }

    try {
      await lastValueFrom(this.paymentService.deletePaymentMethod(paymentMethod.id));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.cardButtonClicked = false;
      return;
    }

    this.paymentMethods = undefined;
    setTimeout(() => {
      this.loadPaymentMethods();
      this.loadSubscriptionData();
      this.cardButtonClicked = false;
    }, 2000);
  }

  loadBillingHisotry() {
    this.billingHistoryDataIsLoading = true;

    // Electricity contracts
    this.paymentService.getBillingHistory((this.lastLoadedPageNumber + 1)).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.listOfBillingHistory.length + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }

          this.listOfBillingHistory = [...this.listOfBillingHistory, ...response.data];
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.billingHistoryDataIsLoading = false;
      },
      complete: () => {
        this.lastLoadedPageNumber++;
        this.billingHistoryDataIsLoading = false;
      }
    });
  }

  getPriceFromStripeTotal(price: number): string {
    return (price / 100).toFixed(2);
  }

  downloadInvoice(invoice_url: string) {
    if (invoice_url) {
      window.open(invoice_url, '_blank');
    }
  }

  async openCustomerPanel() {
    const returnUrl = window.location.href;

    try {
      const response = await lastValueFrom(this.paymentService.getCustomerPortal(returnUrl));

      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code == 'SVC-4004') {
          this.toast.showToast("shared.error", "settings.subscription.customer_missing", 'warning', 4000, true);
          showError = false;
        }
        else if (error.error.code == 'SVC-5002') {
          this.toast.showToast("shared.error", "shared.toast.external_service_not_available", 'error', 4000, true);
          showError = false;
        }
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.cardButtonClicked = false;
      return;
    }
  }

  loadPlanCost() {
    if (!this.activeSubscription) {
      return;
    }

    this.paymentService.getPaymentPlans().subscribe({
      next: (response) => {
        if (response) {
          const matchedPlan = response.find(
            (plan: PaymentPlan) => plan.id === this.activeSubscription?.payment_plan_id
          );

          if (matchedPlan) {
            const billedYearly = this.activeSubscription?.billed_yearly;
            const rawAmount = billedYearly
              ? matchedPlan.price_per_year
              : matchedPlan.price_per_month;

            this.amount = this.decimalPipe.transform(rawAmount, '1.2-2') || rawAmount.toString();
          }
        }
      },
      error: async (error) => {
      },
      complete: () => {
      }
    });
  }

  get billedYearly(): boolean {
    return this.activeSubscription?.billed_yearly ?? false;
  }
}
